import { Link, Outlet, useLocation } from 'react-router';
import { Lock } from 'lucide-react';
import { useFeatureFlags } from '@/hooks/useCapabilities.ts';
import type { FeatureKey } from '@/features/registry.ts';
import { cn } from '@/lib/utils';

/**
 * The grouped-sidebar shell every settings area shares.
 *
 * Extracted from `AdminLayout` when account settings needed the same thing. The
 * sections differ — admin gates on permissions and locks features, account gates on
 * nothing, because it is all yours — but the *shell* is identical, and two hand-kept
 * copies of a nav is how one of them quietly stops matching the other on mobile.
 *
 * Callers decide what is visible. This renders what it is handed.
 */

export interface SettingsNavItem {
  to: string;
  label: string;
  /** SaaS-only feature gate; shows a lock badge. */
  feature?: FeatureKey;
}

export interface SettingsNavSection {
  label: string;
  visible: boolean;
  items: SettingsNavItem[];
}

export function SettingsLayout({
  sections,
  ariaLabel,
}: {
  sections: SettingsNavSection[];
  /** Names the nav for screen readers — "Admin sections", "Account sections". */
  ariaLabel: string;
}) {
  const location = useLocation();
  const hasFeature = useFeatureFlags();

  const visibleSections = sections.filter((s) => s.visible && s.items.length > 0);

  // Nested paths mean a prefix match would light up the parent as well: with
  // `/account/notifications` and `/account/notifications/destinations` both in the
  // list, `startsWith` marks two items active at once. Exact match is what the
  // grouped list needs, and every item here is a leaf.
  const isActive = (to: string) => location.pathname === to;
  const isLocked = (item: SettingsNavItem) => (item.feature ? !hasFeature(item.feature) : false);

  return (
    <div className="flex flex-col md:flex-row min-h-[calc(100vh-4rem)]">
      {/* Mobile: compact horizontal-scrolling strip so the nav doesn't push
          content down a full screen on every page. */}
      <nav
        className="md:hidden flex gap-1 overflow-x-auto border-b border-border-subtle px-3 py-2"
        aria-label={ariaLabel}
      >
        {visibleSections.flatMap((section) =>
          section.items.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              aria-current={isActive(item.to) ? 'page' : undefined}
              className={cn(
                'flex items-center gap-1.5 flex-shrink-0 whitespace-nowrap px-3 py-1.5 rounded-md text-sm transition-colors',
                isActive(item.to) ? 'bg-sev-5/10 text-sev-5 font-medium' : 'hover:bg-accent/40',
              )}
            >
              {item.label}
              {isLocked(item) ? (
                <Lock
                  className="w-3 h-3 flex-shrink-0 text-muted-foreground"
                  aria-label="Available in the full product"
                />
              ) : null}
            </Link>
          )),
        )}
      </nav>

      {/* Desktop: grouped vertical sidebar. */}
      <aside className="hidden md:block md:w-60 shrink-0 md:border-r border-border-subtle p-4 md:p-5">
        <nav className="space-y-5" aria-label={ariaLabel}>
          {visibleSections.map((section) => (
            <div key={section.label}>
              <p className="text-xs uppercase tracking-wider text-muted-foreground font-semibold mb-2">
                {section.label}
              </p>
              <ul className="space-y-0.5">
                {section.items.map((item) => (
                  <li key={item.to}>
                    <Link
                      to={item.to}
                      aria-current={isActive(item.to) ? 'page' : undefined}
                      className={cn(
                        'flex items-center gap-2 px-2 py-1.5 rounded-md text-sm transition-colors',
                        isActive(item.to) ? 'bg-sev-5/10 text-sev-5 font-medium' : 'hover:bg-accent/40',
                      )}
                    >
                      <span className="truncate">{item.label}</span>
                      {isLocked(item) ? (
                        <Lock
                          className="w-3 h-3 ml-auto flex-shrink-0 text-muted-foreground"
                          aria-label="Available in the full product"
                        />
                      ) : null}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </nav>
      </aside>

      <div className="flex-1 min-w-0">
        <Outlet />
      </div>
    </div>
  );
}
